/**
 * ============================================
 * MULTIPLAYER (ONLINE PVP) ANALYTICS HOOK
 * ============================================
 *
 * Loads the snapshot the analytics worker builds once a day
 * (GET /api/v1/multiplayer/summary) and its run status. A manual sync is only
 * QUEUED for the worker; while it is queued/running the status is polled and
 * the snapshot is reloaded once the run finishes.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { BACKEND_URL } from '@/config';

// ============================================
// TYPES
// ============================================

export type MpCell = string | number | boolean | null | number[];

export interface MpUnionCounts {
  reports: number;
  matches: number;
  complete: number;
  oneSide: number;
  bySource: { both: number; s3: number; dynamodb: number };
  conflicts: number;
  excludedCountry: number;
  noDate: number;
  todayOrLater: number;
  s3Editor: number;
  s3PreMatch: number;
}

export interface MpCounts {
  s3: { listed: number; fetched: number; failed: number; storeRows: number; [k: string]: number };
  dynamodb: { events: number; unparseable: number; preMatch: number };
  entry: { presses: number; excludedCountry: number; todayOrLater: number; playerDays: number; players: number };
  union: MpUnionCounts;
}

export interface MpFunnelV221 {
  window: { from: string | null; to: string | null };
  opened: number;
  startedSession: number;
  playersWithReports: number;
  reportsV221: number;
  steps: { step: string; players: number }[];
  byMatchType: Record<string, number[]>;
}

export interface MpEnums {
  matchTypes: string[];
  endReasons: string[];
  inputDevices: string[];
  abilities: string[];
  sources: string[];
  funnelSteps: string[];
}

/** local / editor come from analytics level events; online / editor_online from match reports. */
export type MpPvpMode = 'local' | 'online' | 'editor' | 'editor_online';

/** GET /api/v1/multiplayer/pvp-levels — compact per-player rows. */
export interface MpPvpLevelData {
  version: number;
  dataThrough: string;
  modes: MpPvpMode[];
  /** display names; online maps use the merged map names (Night City, Cube Gun) */
  levels: string[];
  levelModes: Record<string, MpPvpMode>;
  platforms: string[];
  dates: string[];
  buckets: string[];
  /** exclusive upper bound (seconds) of each bucket; null = open-ended */
  bucketUpper: (number | null)[];
  /** [player, dateIdx, platformIdx, levelIdx, entries, roundSeconds...] */
  rows: number[][];
  /** version >= 3: country of each row (index into countries), parallel to rows */
  countries?: string[];
  rowCountries?: number[];
  players: number;
  counts: Record<string, number>;
}

export interface MpPvpLevelsSummary {
  available: boolean;
  rows?: number | null;
  players?: number | null;
}

export interface MpSnapshot {
  available: true;
  version: number;
  generatedAt: string;
  dataThrough: string;
  counts: MpCounts;
  enums: MpEnums;
  reportCols: string[];
  reports: MpCell[][];
  entry: {
    dates: string[];
    versions: string[];
    platforms: string[];
    countries: string[];
    /** [deviceIdx, dateIdx, versionIdx, platformIdx, countryIdx, presses] */
    rows: number[][];
  };
  funnelV221: MpFunnelV221 | null;
  /** every scene ever reported, all time (absent on snapshots built before 8.2.0 final) */
  scenes?: string[];
  /** whether the separate PVP level rows file exists (absent on snapshots built before 8.3.0) */
  pvpLevels?: MpPvpLevelsSummary | null;
  notes: string[];
}

/** One player's report of one match (decoded from snapshot.reports). */
export interface MpReport {
  sid: string;
  slot: number;
  /** index into enums.sources: 0 both, 1 s3, 2 dynamodb */
  src: number;
  date: string;
  /** anonymous device index shared with entry rows; -1 when unknown (S3-only) */
  dev: number;
  plat: string;
  ver: string;
  geo: string;
  region: string;
  scene: string;
  mt: number;
  er: number;
  el: number;
  won: boolean;
  bf: boolean;
  fm: number;
  deaths: number;
  resp: number;
  cp: number;
  ab: number[];
  ping: number;
  pmin: number;
  pmax: number;
  jit: number;
  disc: number;
  bs: number;
  br: number;
  search: number;
  ready: number;
  pauses: number;
  paused: number;
  input: number;
  sn: number;
  sp95: number;
  speak: number;
  s300: number;
  binMed: number;
  boutMed: number;
  binPeak: number;
  boutPeak: number;
  key: string;
  /** 'online' or 'editor_online' */
  mode: MpPvpMode;
}

export type MpRunState = 'never_run' | 'running' | 'succeeded' | 'kept_previous' | 'failed' | 'skipped' | 'unknown';

export interface MpStatus {
  state: MpRunState;
  trigger?: string;
  full?: boolean;
  startedAt?: string;
  finishedAt?: string | null;
  durationSec?: number;
  error?: string | null;
  dataThrough?: string;
  counts?: MpCounts;
  queued: boolean;
  stale: boolean;
  nextScheduledRun: string;
}

export interface MpSeries {
  ping: number[];
  bandwidthIn: number[];
  bandwidthOut: number[];
}

export interface MpMatchDetail {
  sid: string;
  reports: { report: Omit<MpReport, 'dev'>; series: MpSeries | null }[];
  enums: MpEnums;
  sources: string[];
  generatedAt: string;
}

// ============================================
// API
// ============================================

async function getJson<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${BACKEND_URL}${endpoint}`, { headers: { 'Content-Type': 'application/json' } });
  if (!response.ok) {
    let message = `API error: ${response.status}`;
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch { /* keep status message */ }
    throw new Error(message);
  }
  return response.json();
}

export function fetchMatchDetail(sessionId: string): Promise<MpMatchDetail> {
  return getJson<MpMatchDetail>(`/api/v1/multiplayer/matches/${encodeURIComponent(sessionId)}`);
}

/** Loads the PVP level rows when the snapshot says they exist; reloads when the
 *  snapshot changes (a new run rewrites both). */
export function usePvpLevels(available: boolean, snapshotVersion: string | null) {
  const [data, setData] = useState<MpPvpLevelData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!available) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getJson<MpPvpLevelData | { available: false; message?: string }>('/api/v1/multiplayer/pvp-levels')
      .then(d => { if (!cancelled) setData('rows' in d ? d : null); })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load PVP level data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [available, snapshotVersion]);

  return { data, loading, error };
}

// ============================================
// HOOK
// ============================================

export function useMultiplayerAnalytics() {
  const [snapshot, setSnapshot] = useState<MpSnapshot | null>(null);
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<MpStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [isQueueing, setIsQueueing] = useState(false);
  // undefined = no status seen yet; otherwise finishedAt of the last run accounted for
  const lastRunSeen = useRef<string | null | undefined>(undefined);

  const loadSummary = useCallback(async () => {
    try {
      const data = await getJson<MpSnapshot | { available: false; message?: string }>('/api/v1/multiplayer/summary');
      if (data.available) {
        setSnapshot(data);
        setUnavailableMessage(null);
      } else {
        setSnapshot(null);
        setUnavailableMessage(data.message || 'No multiplayer data yet.');
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load multiplayer data');
    }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await getJson<MpStatus>('/api/v1/multiplayer/status'));
    } catch (err) {
      console.error('Multiplayer status fetch error:', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      await Promise.all([loadSummary(), loadStatus()]);
      if (!cancelled) setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [loadSummary, loadStatus]);

  // Server time of a sync THIS tab queued and is still waiting on. The queued flag
  // alone isn't enough: the worker removes the request file before the stored
  // status says "running" (and the web side caches status for ~10 s), so a poll in
  // that gap looks idle. We keep waiting until a run that started at/after the
  // request has finished.
  const [pendingRequestAt, setPendingRequestAt] = useState<string | null>(null);
  const pendingCovered = !!pendingRequestAt && !!status?.startedAt
    && status.startedAt >= pendingRequestAt && status.state !== 'running';

  const isBusy = (!!status && (status.queued || (status.state === 'running' && !status.stale)))
    || (!!pendingRequestAt && !pendingCovered);

  useEffect(() => {
    if (pendingCovered) setPendingRequestAt(null);
  }, [pendingCovered]);

  // Give up waiting if the worker never picks the request up (e.g. it is down).
  useEffect(() => {
    if (!pendingRequestAt) return;
    const id = window.setTimeout(() => {
      setPendingRequestAt(null);
      setSyncMessage('The analytics worker has not started the queued sync yet — check that lokko-analytics is running.');
    }, 15 * 60 * 1000);
    return () => window.clearTimeout(id);
  }, [pendingRequestAt]);

  // Poll while a run is queued or running.
  useEffect(() => {
    if (!isBusy) return;
    const id = window.setInterval(loadStatus, 5000);
    return () => window.clearInterval(id);
  }, [isBusy, loadStatus]);

  // The first status we receive describes the snapshot loaded alongside it, so it
  // only sets the baseline. Reload when a LATER run finishes successfully while the
  // tab is open. (Comparing finishedAt with generatedAt can't work: a run always
  // finishes a few seconds after it stamps the snapshot.)
  useEffect(() => {
    if (!status) return;
    if (lastRunSeen.current === undefined) {
      lastRunSeen.current = status.finishedAt ?? null;
      return;
    }
    if (isBusy || status.state !== 'succeeded' || !status.finishedAt) return;
    if (status.finishedAt === lastRunSeen.current) return;
    lastRunSeen.current = status.finishedAt;
    loadSummary();
  }, [status, isBusy, loadSummary]);

  const queueSync = useCallback(async (full: boolean) => {
    setIsQueueing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/multiplayer/sync${full ? '?full=1' : ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const body = await response.json().catch(() => ({}));
      setSyncMessage(body?.message || (response.ok ? 'Queued' : `Sync request failed (${response.status})`));
      if (response.ok && body?.requestedAt) setPendingRequestAt(body.requestedAt);
    } catch (err) {
      setSyncMessage(err instanceof Error ? err.message : 'Sync request failed');
    } finally {
      setIsQueueing(false);
      loadStatus();
    }
  }, [loadStatus]);

  return {
    snapshot,
    unavailableMessage,
    status,
    isLoading,
    error,
    isBusy,
    isQueueing,
    syncMessage,
    queueSync,
    reload: loadSummary,
  };
}
